import { type FC, type HTMLAttributes } from 'react'
import { useIntl } from 'react-intl'
import { useTheme } from '@mui/material/styles'

import Separator from '@DS/components/components/foundations/shapes/separator/Separator'
import { useIsMediumUp } from '@Hooks/useResponsive'

import InputPlace from '../atoms/InputPlace'
import Swap from '../atoms/Swap'
import { type ODSearchCommonProps } from '../common/ODSearch.types'
import * as commonStyles from '../common/styles'

import * as styles from './styles'

type ODSearchStickyProps = ODSearchCommonProps

const ODSearchSticky: FC<ODSearchStickyProps & HTMLAttributes<HTMLDivElement>> = ({
  elevation = 'onBackground',
  originInput: { value: originInputValue, ...restOriginInput },
  destinationInput: { value: destinationInputValue, ...restDestinationInput },
  onSwap,
  swapProps,
  ...rest
}) => {
  const theme = useTheme()
  const { formatMessage } = useIntl()
  const isDesktop = useIsMediumUp(theme)

  const isSwapVisible = typeof onSwap === 'function'

  const handleSwap = () =>
    isSwapVisible &&
    onSwap(
      { origin: originInputValue, destination: destinationInputValue },
      { origin: destinationInputValue, destination: originInputValue },
    )

  return (
    <div {...rest} css={styles.odSearchStickyResponsive}>
      <fieldset css={styles.odsearchStickyFieldset(theme)} aria-label={formatMessage({ id: 'home_searchField' })}>
        <InputPlace
          css={[
            commonStyles.inputPlace(theme, {
              disabled: restOriginInput.disabled,
              elevation,
            }),
            styles.originInputPlaceStickyResponsive(theme, isSwapVisible),
          ]}
          {...restOriginInput}
          value={originInputValue}
          required={!originInputValue}
          prefix={formatMessage({ id: 'departure' })}
          placeholder={formatMessage({ id: 'odSearch_origin_hint_SrLabel' })}
        />
        <InputPlace
          css={[
            commonStyles.inputPlace(theme, {
              disabled: restDestinationInput.disabled,
              elevation,
            }),
            styles.destinationInputPlaceStickyResponsive(theme, isSwapVisible),
          ]}
          {...restDestinationInput}
          value={destinationInputValue}
          required={!destinationInputValue}
          prefix={formatMessage({ id: 'arrival' })}
          placeholder={formatMessage({ id: 'odSearch_destination_hint_SrLabel' })}
        />
        <Separator
          css={styles.separatorStickyResponsive(theme)}
          background={elevation === 'onBackground' ? 'default' : 'paper'}
          isNotOutline
        />
        {isSwapVisible && (
          <Swap
            {...swapProps}
            css={styles.absoluteStickyResponsive(theme)}
            ariaLabelOrigin={originInputValue}
            ariaLabelDestination={destinationInputValue}
            onClick={handleSwap}
            isHorizontal={isDesktop}
          />
        )}
      </fieldset>
    </div>
  )
}

export default ODSearchSticky
